// client/src/shared/stores/send-queue.flush.ts
//
// Décision PURE de flush de la file d'attente d'envoi (#10587), extraite de `InputBar` pour être
// testable par isolation (harnais `test-file-attente-isolation.ts`) — l'effet React et le test
// exécutent EXACTEMENT la même logique, jamais une copie.

import type { AgentStatus } from './status.store'

/**
 * Un tour est terminé (donc on PEUT envoyer le message suivant) quand le statut de session est
 * retombé à un état terminal ET qu'aucun streaming n'est en cours. Le daemon garantit une émission
 * status:idle à la fin de CHAQUE tour — fin normale, erreur, ET après abort (index.ts:955/978/981,
 * catch d'abort l.1408-1409) — donc ce prédicat couvre le succès, l'erreur et l'abort.
 */
export function isTurnEnded(status: AgentStatus, isStreaming: boolean): boolean {
  return (status === 'idle' || status === 'error' || status === 'closed') && !isStreaming
}

/** État de la garde anti-burst : un seul envoi par période idle. */
export interface FlushGate {
  /** true = on a déjà consommé (envoyé) un message pendant la période terminale courante. */
  consumed: boolean
}

export const initialFlushGate: FlushGate = { consumed: false }

/**
 * Fait avancer la garde de flush d'un pas, en fonction de l'état courant du tour et de la présence
 * d'un message en file. Retourne s'il faut envoyer MAINTENANT (`flush`) et la garde suivante.
 *
 * Invariants (garde-fous Murphy) :
 *  - Un seul envoi par période idle (`consumed`) : le message envoyé refait passer le statut à
 *    active, ce qui réarme la garde pour le tour suivant → un message par tour, jamais de rafale.
 *  - Tant que le tour n'est pas terminé, la garde est réarmée (`consumed:false`).
 *  - Rien en file → aucun envoi, garde inchangée (on reste prêt à flusher dès qu'un message arrive
 *    pendant cette même période idle — cas du retour sur un onglet en arrière-plan).
 */
export function stepFlushGate(
  gate: FlushGate,
  turnEnded: boolean,
  hasQueued: boolean,
): { flush: boolean; gate: FlushGate } {
  if (!turnEnded) return { flush: false, gate: initialFlushGate }
  if (gate.consumed) return { flush: false, gate }
  if (!hasQueued) return { flush: false, gate }
  return { flush: true, gate: { consumed: true } }
}
